// Shared API contract types — used by both server and web.

export type MapLabelMode = 'local' | 'mine';

export interface TripView {
  token: string;
  name: string;
  ownerId: string | null;
  linkEditEnabled: boolean;
  activeCityId: number | null;
  /** User-picked POI whose photo represents the trip on the dashboard.
   *  Null means "fall back to the rating-based default." */
  coverPoiId: number | null;
  /** Map label language mode (see migration 013). 'local' = derive
   *  language from trip region; 'mine' = viewer's browser language. */
  mapLabelMode: MapLabelMode;
  createdAt: string;
  updatedAt: string;
  viewerUserId: string | null;
  editable: boolean;
}

export interface ViewportRect {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface TripCityView {
  id: number;
  name: string;
  lat: number;
  lng: number;
  regionCode: string;
  viewport: ViewportRect;
}

export interface ListCitiesResponse {
  cities: TripCityView[];
}

export interface AddCityRequest {
  placeId: string;
  sessionToken: string;
  setActive?: boolean;
}

export interface AddCityResponse {
  city: TripCityView;
  activeCityId: number | null;
}

export interface SetActiveCityRequest {
  cityId: number | null;
}

export interface CityAutocompleteRequest {
  query: string;
  sessionToken: string;
}

export interface CityAutocompleteSuggestion {
  placeId: string;
  primary: string;
  secondary: string;
}

export interface CityAutocompleteResponse {
  suggestions: CityAutocompleteSuggestion[];
}

export interface TextSearchRequest {
  query: string;
}

export interface TextSearchResult {
  placeId: string;
  name: string;
  formattedAddress: string;
  category: string | null;
  lat: number;
  lng: number;
  rating: number | null;
  photoReference: string | null;
  alreadyBookmarked: boolean;
}

export interface TextSearchResponse {
  results: TextSearchResult[];
}

export interface CreateTripResponse {
  token: string;
}

/** A single stop in the Route view's ordered list. "here" is a virtual
 *  stop representing the user's current location — we don't know its
 *  coords; the Maps handoff URL just omits the origin/source param and
 *  the Maps app fills it in natively. "here" is constrained to position 0
 *  in the list. */
export type RouteStop = { kind: 'here' } | { kind: 'poi'; poiId: number };

/** Travel mode passed through to Apple/Google Maps via the handoff URL.
 *  Walk / Drive / Transit; bicycling is Google-only and not exposed yet. */
export type RouteMode = 'walking' | 'driving' | 'transit';

export interface DashboardTripView {
  token: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Total places bookmarked on the trip. */
  poiCount: number;
  /** Active city if set, otherwise the first city added to the trip,
   *  null when no cities exist. Used as the dashboard card subhead. */
  primaryCityName: string | null;
  /** Photo reference of the oldest bookmarked POI that has one, used as
   *  the trip card's cover image. Null when no POI on the trip carries
   *  a usable photo. */
  coverPhotoReference: string | null;
}

export interface UserDashboardView {
  user: { token: string; email: string };
  trips: DashboardTripView[];
}

export interface SaveToDashboardResponse {
  user: { token: string };
}

export interface ErrorResponse {
  error: string;
}

export type GroupTint =
  | 'peach'
  | 'rose'
  | 'coral'
  | 'yellow'
  | 'mint'
  | 'teal'
  | 'sky'
  | 'lavender';

export const GROUP_TINTS: GroupTint[] = [
  'peach',
  'rose',
  'coral',
  'yellow',
  'mint',
  'teal',
  'sky',
  'lavender',
];

/** Brand-primary hex (Rausch, --color-primary). Used as the default map
 *  pin fill — SVG fills can't resolve CSS custom properties. */
export const BRAND_PRIMARY_HEX = '#ff385c';

/** Legal-link blue hex (--color-legal-link). Used for the "you are here"
 *  marker on the map. */
export const ME_MARKER_HEX = '#428bff';

/** Hex equivalents of the .dot--<tint> CSS variables. Kept here (not as
 *  CSS vars at the call site) because SVG fills and canvas paints can't
 *  resolve CSS custom properties. */
export const GROUP_TINT_HEX: Record<GroupTint, string> = {
  peach: '#c2511c',
  rose: '#c93982',
  coral: '#d63a4a',
  yellow: '#8a6d12',
  mint: '#2d8d4d',
  teal: '#1d8780',
  sky: '#2074c4',
  lavender: '#6b4ba8',
};

export interface GroupView {
  id: number;
  name: string;
  tint: GroupTint;
}

export interface PoiView {
  id: number;
  placeId: string;
  name: string;
  customName: string | null;
  category: string | null;
  lat: number;
  lng: number;
  rating: number | null;
  photoReference: string | null;
  notes: string | null;
  cityId: number | null;
  /** Locality extracted from the POI's address_components (server-side via
   *  the same `extractLocality` heuristic used to suggest unmapped cities).
   *  Null when address_components doesn't yield a usable locality. Drives
   *  the auto-city auto-group's "fallback when the user hasn't added the
   *  locality as a trip city" branch. */
  locality: string | null;
  /** Google's localized formatted_address (e.g., "84-92 R. de Belém,
   *  1300-085 Lisboa"). Null on POIs bookmarked before migration 009. */
  formattedAddress: string | null;
  /** ISO 3166-1 alpha-2 country code extracted from the POI's
   *  address_components (e.g., "JP", "PT"). Drives the map-language
   *  POI-consensus fallback when no city is active. Null when the
   *  components don't yield a country (rare; some landmarks). */
  regionCode: string | null;
  /** Pre-formatted weekday strings from Google
   *  (`regularOpeningHours.weekdayDescriptions`). Null when the place
   *  has no published hours, or for POIs bookmarked before migration
   *  011 (we don't backfill — the user can re-bookmark to refresh). */
  openingHours: string[] | null;
  /** Structured open/close periods (`regularOpeningHours.periods`).
   *  Used for client-side "is open now" computation; never displayed
   *  directly. Null on legacy rows (pre-014); lazy-refresh backfills. */
  openingPeriods: OpeningPeriod[] | null;
  /** POI's UTC offset in minutes — anchors "open now" to the POI's
   *  local time, not the viewer's browser time. Null on legacy rows
   *  (pre-014); lazy-refresh backfills. */
  utcOffsetMinutes: number | null;
  /** Unix epoch ms of the last Places Details fetch (bookmark or
   *  refresh). Null on legacy rows. Stale-detection compares against
   *  Date.now() to decide whether to fire a background refresh when
   *  the detail sheet opens. */
  detailsFetchedAt: number | null;
  createdAt: string;
  groupIds: number[];
}

/** Structured day/time entry from Google's regularOpeningHours.periods.
 *  See places.ts (server) for full docs. */
export interface OpeningPeriod {
  open: { day: number; hour: number; minute: number };
  close?: { day: number; hour: number; minute: number };
}

/** Preview of a place before bookmarking. Surface for the two-step
 *  search → preview → bookmark flow. */
export interface PlacePreview {
  placeId: string;
  name: string;
  category: string | null;
  lat: number;
  lng: number;
  rating: number | null;
  photoReference: string | null;
  formattedAddress: string | null;
  /** Pre-formatted weekday strings from Google's
   *  `regularOpeningHours.weekdayDescriptions`. Null when the place
   *  has no published hours. We don't parse periods — Google's
   *  localized strings ship as-is. */
  openingHours: string[] | null;
  /** Structured periods + UTC offset — same shape as PoiView's
   *  fields, for the open-now badge in the preview sheet. */
  openingPeriods: OpeningPeriod[] | null;
  utcOffsetMinutes: number | null;
  alreadyBookmarked: boolean;
}

export interface PlacePreviewRequest {
  placeId: string;
  sessionToken: string;
}

export interface PlacePreviewResponse {
  preview: PlacePreview;
}

export interface ResolveUrlRequest {
  url: string;
}

export type ResolveUrlReason =
  | 'unsupported'
  | 'not_found'
  | 'list_unsupported'
  | 'parse_failed';

export interface ResolveUrlResponse {
  placeId: string;
  displayName: string;
}

export interface UpdatePoiRequest {
  customName?: string | null;
  notes?: string | null;
  cityId?: number | null;
}

export interface CreateGroupRequest {
  name: string;
  tint: GroupTint;
}

export interface UpdatePoiGroupsRequest {
  groupIds: number[];
}

export interface AutocompleteSuggestion {
  placeId: string;
  primary: string;
  secondary: string;
  alreadyBookmarked: boolean;
  /** Most-specific non-generic type, raw form (e.g., "shopping_mall"). */
  category: string | null;
  /** Distance in meters from the active city, when one is set. */
  distanceMeters: number | null;
}

export interface AutocompleteRequest {
  query: string;
  sessionToken: string;
}

export interface AutocompleteResponse {
  suggestions: AutocompleteSuggestion[];
}

export interface BookmarkRequest {
  placeId: string;
  sessionToken: string;
  /** Localized name the user saw in autocomplete / text-search; preserved as the POI's name. */
  displayName?: string;
}

export interface BookmarkResponse {
  poi: PoiView;
  alreadyBookmarked: boolean;
  /** Locality of this POI if it isn't already a city on the trip — used to drive the auto-suggest banner. */
  unmappedLocality?: { name: string } | null;
}
